import { WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { BottomSheet, BottomSheetLoadingContent } from "@clipboard-health/ui-components";
import { type UseModalState } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent } from "@mui/material";
import { useRef } from "react";

import { PERSONAL_ID_VERIFICATION_POLLING_CONFIG } from "./constants";
import { type OnboardingPersonalIdVerificationStep } from "./types";
import { useGetPersonalIdRequirement } from "./useGetPersonalIdRequirement";
import { getStepFromRequirementStatus } from "./utils";

interface DocumentDetailsStripeIdentityVerificationInProgressBottomSheetProps {
  modalState: UseModalState;
  setCurrentStep: (step: OnboardingPersonalIdVerificationStep) => void;
}

const { minPollingTimeMs, maxPollingTimeMs, pollingIntervalMs } =
  PERSONAL_ID_VERIFICATION_POLLING_CONFIG;

export function OnboardingPersonalIdVerificationInProgressBottomSheet({
  modalState,
  setCurrentStep,
}: DocumentDetailsStripeIdentityVerificationInProgressBottomSheetProps) {
  const startedAt = useRef(Date.now());

  useGetPersonalIdRequirement({
    options: {
      refetchInterval: (data) => {
        const elapsed = Date.now() - startedAt.current;
        const status = data?.[0]?.status;

        // Wait at least 1 second before considering redirection
        if (elapsed >= minPollingTimeMs) {
          if (status === WorkerRequirementStatus.APPROVED) {
            setCurrentStep(getStepFromRequirementStatus(status));
            return false; // Stop polling
          }

          if (elapsed >= maxPollingTimeMs) {
            // Redirect to correct step based on status and time is up
            setCurrentStep(
              isDefined(status) ? getStepFromRequirementStatus(status) : "start-verification"
            );
            return false;
          }
        }

        return pollingIntervalMs;
      },
    },
  });

  return (
    <BottomSheet
      canDismiss={false}
      modalState={modalState}
      paperSx={{
        ...getSafeAreaPaddings({ bottom: true }),
      }}
    >
      <DialogContent sx={{ paddingX: 5 }}>
        <BottomSheetLoadingContent
          title="Verifying Your Identity"
          description="This should only take a few seconds"
        />
      </DialogContent>
    </BottomSheet>
  );
}
